import css from "./ProjectPage.module.scss";

import { motion } from "framer-motion";
import { animPropsNames } from "../../utils/animation";

import { useSelector } from "react-redux";
import { get } from "../../store/selectors";
import { Link, useLoaderData } from "react-router-dom";

import { useState } from "react";
import { ImageWebp, TagsBar, MetaIcons } from "../../components";
import { ProjectData, defaultProject } from "router/handleProjetsData";

export const ProjectPage = () => {
	const reducedMotion = useSelector(get.reducedMotion);
	const [project] = useState((useLoaderData() ?? defaultProject) as ProjectData); // need to "cache" the data (AnimatePresence transition + change route => useLoaderData() = undefined)

	const goBack = () => window.history.back();
	return (
		<motion.div className={css._} variants={projectTransition} {...(!reducedMotion && animPropsNames)}>
			{project.logo && <Img className={css.logo} imgData={project.logo} />}
			<hgroup>
				<h1>{project.title}</h1>
				{project.subtitle && <p>{project.subtitle}</p>}
			</hgroup>
			<motion.nav>
				<button onClick={goBack}>Go back</button>
				{project.tags.map(tag => (
					<Link to={`/work/about/${tag}`} key={tag}>
						{capitalize(tag)}
					</Link>
				))}
			</motion.nav>
			<div className={css.content}>
				<Img imgData={project.img} variants={childTransition} />
				<div>
					<TagsBar tags={project.tags} />
					<motion.p variants={childTransition}>{project.description}</motion.p>
					<motion.p variants={childTransition}>{project.goal}</motion.p>
					<ProcessSection data={project.process} />
					<motion.p variants={childTransition}>{project.result}</motion.p>
					<motion.p variants={childTransition}>{project.next}</motion.p>
					<MetaSection data={project.meta} />
				</div>
			</div>
		</motion.div>
	);
};

const Img = ({ imgData, ...props }) => {
	const getSrcset = (url, source) => {
		return source
			? Object.keys(source).reduce(
					(srcset, size) => `${srcset}, ${require(`../../assets/images/${url + source[size]}`)} ${size}`,
					""
			  )
			: "";
	};

	return imgData.webp || imgData.jpg || imgData.png ? (
		<motion.div {...props}>
			<ImageWebp
				webp={getSrcset(imgData.url, imgData.webp)}
				png={getSrcset(imgData.url, imgData.png)}
				jpg={getSrcset(imgData.url, imgData.jpg)}
				alt={imgData.alt}
				title={imgData.title}
			/>
		</motion.div>
	) : (
		<motion.div className={css.noImg} {...props}>
			<span>{"⊘"}</span>
			<span className={css.alt}>{imgData.alt}</span>
		</motion.div>
	);
};

const MetaSection = ({ data }) => {
	if (!data) return null;

	return (
		<motion.div className={css.meta} variants={childTransition}>
			{data.date && (
				<div className={css.metaItem}>
					<MetaIcons.Date />
					<p>{data.date}</p>
				</div>
			)}
			{data.role && (
				<div className={css.metaItem}>
					<MetaIcons.Role />
					<p>{data.role.join(", ")}</p>
				</div>
			)}
			{data.tools && (
				<div className={css.metaItem}>
					<MetaIcons.Tool />
					<p>{data.tools.join(", ")}</p>
				</div>
			)}
			{data.link && (
				<div className={css.metaItem}>
					<MetaIcons.Link />
					<a href={data.link} target="_blank" rel="noopener noreferrer">
						View project
					</a>
				</div>
			)}
		</motion.div>
	);
};

const ProcessSection = ({ data }) => {
	if (!data) return null;

	return (
		<motion.div className={css.process} variants={childTransition}>
			{data.design && (
				<div>
					<h3>Design</h3>
					<ul>
						{data.design.map((item, index) => (
							<li key={index}>{item}</li>
						))}
					</ul>
				</div>
			)}
			{data.dev && (
				<div>
					<h3>Development</h3>
					<ul>
						{data.dev.map((item, index) => (
							<li key={index}>{item}</li>
						))}
					</ul>
				</div>
			)}
			{data.challenges && (
				<div>
					<h3>Challenges</h3>
					<ul>
						{data.challenges.map((item, index) => (
							<li key={index}>{item}</li>
						))}
					</ul>
				</div>
			)}
		</motion.div>
	);
};

function capitalize(string = "") {
	const firstLetterCap = string.charAt(0).toUpperCase();
	const rest = string.substring(1);
	return firstLetterCap + rest;
}

const projectTransition = {
	initial: { scale: 0.4, opacity: 0 },
	animate: { scale: 1, opacity: 1, transition: { type: "spring" as const, duration: 0.7, staggerChildren: 0.1 } },
	exit: { scale: 0.4, opacity: 0, transition: { duration: 0.2 } },
};

const childTransition = {
	initial: { scale: 0.7, opacity: 0 },
	animate: { scale: 1, opacity: 1, transition: { type: "spring" as const } },
	exit: { scale: 0.7, opacity: 0, transition: { duration: 0.2 } },
};
